import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Check, ChevronDown, Sparkles } from "lucide-react";
import { PlanetMark } from "@/components/planet-mark";
import { SiteFooter } from "@/components/site-footer";
import { SignInDialog } from "@/components/sign-in-dialog";
import { RequestAccessDialog } from "@/components/request-access-dialog";
import { ContactModal } from "@/components/contact-modal";
import { COMPARISON, PLANS, type Plan } from "@/lib/plans";

const CANONICAL = "https://pyng.co/pricing";
const TITLE = "Pricing — Pyng";
const DESCRIPTION =
  "Start free with 5 people and the Basic Signal Engine. Pro is $29/mo for 250 people scanned daily, Live Context from your calendar and inbox, and 25 Deep Watch slots. Team from $59 a seat.";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "product" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Pyng",
          description: DESCRIPTION,
          brand: { "@type": "Brand", name: "Pyng" },
          offers: [
            { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", url: CANONICAL },
            { "@type": "Offer", name: "Pro", price: "29", priceCurrency: "USD", url: CANONICAL },
            { "@type": "Offer", name: "Team & Enterprise", price: "59", priceCurrency: "USD", url: CANONICAL },
          ],
        }),
      },
    ],
  }),
});

// Plan definitions are shared with in-app Settings so the two never diverge.

const STATS: Array<{ value: string; label: string }> = [
  { value: "40+", label: "signal types watched — role moves, funding, launches, press" },
  { value: "30 days", label: "freshness rule — nothing older ever reaches your feed" },
  { value: "< 60s", label: "to add a person with full context and a first signal" },
  { value: "0", label: "contacts sold, shared, or used to train outside models" },
];

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "What do I actually get for free?",
    a: "Five people, watched for real by the Basic Signal Engine™. Everyone you add gets a full first scan — role, company, links, and a first signal — and after that they're re-scanned every few days. You get one reason to reach out a day with the message already drafted, unlimited memories and voice memos, full signal history, and one person on Deep Watch who is read daily.",
  },
  {
    q: "What does Pro add?",
    a: "The Pro Signal Engine™. Two hundred and fifty people instead of five, every one of them scanned daily, up to ten reasons a day, twenty-five Deep Watch slots, interest and topic monitoring beyond your people, and Live Context — Pyng reads your calendar and inbox so it can prep you before a meeting, nudge you after it, and know when you last actually spoke.",
  },
  {
    q: "Basic vs Pro Signal Engine™ — what's the difference?",
    a: "The Basic Signal Engine™, on Free, reads the public web plus whatever you tell Pyng — memories, voice memos, notes — and re-reads your people every few days. The Pro Signal Engine™ reads all 250 of your people daily, watches your saved interests and topics, and adds Live Context: your Google Calendar and Gmail for meeting prep, post-meeting hooks, true last-touch dates, and sending follow-ups from your own address.",
  },
  {
    q: "What is Deep Watch, and what is a Boost?",
    a: "Deep Watch is the star next to a name. Those people are read daily and several times over, so their news lands the same day — Free includes 1 slot, Pro 25, Team 50 or more per seat. A Boost is an on-demand re-scan of one person, right now: 3 a month on Free, 50 on Pro.",
  },
  {
    q: "How fresh are the signals?",
    a: "Only things that actually happened recently make the feed. A signal has to carry a real publication date and be under thirty days old before Pyng will build a reason to reach out from it, so you're never handed a headline from 2021.",
  },
  {
    q: "Does Pyng ever send messages for me?",
    a: "No. Every draft waits for you. Pyng writes, you read, edit, and send — nothing leaves on its own, on any plan.",
  },
  {
    q: "Can I switch between monthly and annual?",
    a: "Yes. Annual is 20% cheaper than monthly. Switch at any time and we prorate the difference.",
  },
  {
    q: "How does Team billing work?",
    a: "Team is per seat, per month, starting at $59. Add or remove teammates whenever you like and billing adjusts on the next cycle. Talk to us and we'll size it with you; invoice billing is available.",
  },
  {
    q: "Do you offer a trial?",
    a: "Every new account starts on a 14-day Pro trial — no card required. You'll be reminded before anything is charged, and you drop to Free rather than losing access. If you are charged and unhappy within 14 days, email support@pyng.co and we'll refund it. Team pilots are arranged with us directly.",
  },
  {
    q: "What happens if I downgrade?",
    a: "Nothing is deleted. People beyond your new plan's limit are paused from scanning and you choose who stays active. Reactivate anytime by upgrading again.",
  },
  {
    q: "Do you train AI on my data?",
    a: "Never. Your contacts, memories and voice memos are yours. They are not shared, not sold, and not used to train external models.",
  },
];

function PricingPage() {
  const [annual, setAnnual] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const openPlan = (plan: Plan) => {
    if (plan.action === "contact") setContactOpen(true);
    else setRequestOpen(true);
  };

  return (
    <main className="relative min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
            <PlanetMark size={24} className="rounded-lg" />
            Pyng
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <a href="/#magic-moment" className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline">
              How it works
            </a>
            <Link to="/pricing" className="hidden font-medium text-foreground sm:inline">
              Pricing
            </Link>
            <Link to="/faq" className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline">
              FAQ
            </Link>
            <button
              type="button"
              onClick={() => setSignInOpen(true)}
              className="inline-flex items-center justify-center rounded-full border border-border bg-card/60 px-4 py-1.5 text-[13px] font-medium text-foreground/90 transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground"
            >
              Sign in
            </button>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-5 pt-16 text-center sm:px-6 sm:pt-24">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-neon/80">Plans</p>
        <h1 className="mt-3 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-5xl">
          Invest in the people who change your{" "}
          <span className="text-accent-neon">trajectory</span>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-pretty text-[15px] leading-relaxed text-muted-foreground">
          Start free with the five people who matter most and the Basic Signal Engine™. Upgrade to
          the Pro Signal Engine™ for more people, daily scanning on all of them, and Live Context
          from your calendar and inbox.
        </p>

        <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-border bg-card/60 p-1 text-[12px]">
          <button
            type="button"
            onClick={() => setAnnual(false)}
            aria-pressed={!annual}
            className={`rounded-full px-4 py-1.5 font-medium uppercase tracking-wider transition-colors ${
              !annual ? "bg-accent-neon-soft text-accent-neon" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Pay monthly
          </button>
          <button
            type="button"
            onClick={() => setAnnual(true)}
            aria-pressed={annual}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-medium uppercase tracking-wider transition-colors ${
              annual ? "bg-accent-neon-soft text-accent-neon" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Pay annually
            <span className="rounded-full bg-accent-neon/20 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-accent-neon">
              −20%
            </span>
          </button>
        </div>
      </section>

      <PlanCarousel annual={annual} onSelect={openPlan} />

      {/* Bragging stats */}
      <section className="mx-auto mt-24 max-w-6xl px-5 sm:px-6">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-border bg-border/60 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="bg-background/80 px-5 py-7 text-center">
              <p className="text-3xl font-semibold tracking-tight text-accent-neon">{s.value}</p>
              <p className="mx-auto mt-2 max-w-[18ch] text-[12px] leading-relaxed text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Side-by-side */}
      <section className="mx-auto mt-24 max-w-5xl px-5 sm:px-6">
        <h2 className="text-center text-2xl font-semibold tracking-tight text-foreground">
          Compare the plans
        </h2>
        <div className="no-scrollbar mt-8 overflow-x-auto rounded-3xl border border-border bg-card/40">
          <table className="w-full min-w-[560px] border-collapse text-left">
            <thead>
              <tr className="border-b border-border">
                <th className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Feature
                </th>
                {PLANS.map((p) => (
                  <th
                    key={p.id}
                    className={`px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      p.popular ? "text-accent-neon" : "text-muted-foreground"
                    }`}
                  >
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.label} className="border-b border-border/60 last:border-0">
                  <th scope="row" className="px-5 py-3 text-[13px] font-normal text-foreground/85">
                    {row.label}
                  </th>
                  <td className="px-4 py-3 text-[13px] text-muted-foreground">{row.free}</td>
                  <td className="px-4 py-3 text-[13px] text-foreground">{row.pro}</td>
                  <td className="px-4 py-3 text-[13px] text-muted-foreground">{row.team}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-[11.5px] text-muted-foreground/80">
          Deep Watch people are read daily on every plan — that's what the star buys.
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto mt-24 max-w-3xl px-5 sm:px-6">
        <h2 className="text-center text-2xl font-semibold tracking-tight text-foreground">Pricing questions</h2>
        <div className="mt-8 space-y-2.5">
          {FAQS.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-border bg-card/50 px-5 py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground">
                {f.q}
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 group-open:text-accent-neon" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto mt-24 max-w-3xl px-5 sm:px-6">
        <div className="rounded-3xl border border-accent-neon/25 bg-accent-neon/[0.05] px-6 py-12 text-center shadow-[0_0_60px_-30px_var(--accent-neon)]">
          <h2 className="text-balance text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Get started for <span className="text-accent-neon">free</span>
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
            Add five people. Pyng scans each one the moment you add them, then hands you your first
            reason to reach out.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-7 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 sm:w-auto"
            >
              <Sparkles className="h-4 w-4" /> Get started for free
            </button>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="inline-flex h-11 w-full items-center justify-center rounded-full border border-border px-7 text-sm font-medium text-foreground/90 transition-colors hover:border-foreground/30 sm:w-auto"
            >
              Talk to us
            </button>
          </div>
          <p className="mt-4 text-[11.5px] text-muted-foreground/80">No card required · Cancel anytime</p>
        </div>
      </section>

      <SiteFooter />

      <SignInDialog open={signInOpen} onOpenChange={setSignInOpen} />
      <RequestAccessDialog open={requestOpen} onOpenChange={setRequestOpen} />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

/** Swipable on mobile (scroll-snap), 4-up grid from md. */
function PlanCarousel({ annual, onSelect }: { annual: boolean; onSelect: (plan: Plan) => void }) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);

  const scrollTo = (index: number) => {
    const el = scrollerRef.current;
    const card = el?.children[index] as HTMLElement | undefined;
    if (!el || !card) return;
    card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((child, i) => {
      const c = child as HTMLElement;
      const dist = Math.abs(c.offsetLeft + c.clientWidth / 2 - centre);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setActive(best);
  };

  return (
    <section className="mt-12">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="no-scrollbar flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto scroll-smooth px-5 pb-2 pt-4 md:mx-auto md:max-w-5xl md:grid md:grid-cols-3 md:overflow-visible md:px-6"
      >
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} annual={annual} onSelect={onSelect} />
        ))}
      </div>

      {/* Dots — mobile only */}
      <div className="mt-4 flex items-center justify-center gap-2 md:hidden">
        {PLANS.map((plan, i) => (
          <button
            key={plan.id}
            type="button"
            aria-label={`Show ${plan.name} plan`}
            onClick={() => scrollTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === active ? "w-6 bg-accent-neon" : "w-1.5 bg-foreground/25"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function PlanCard({ plan, annual, onSelect }: { plan: Plan; annual: boolean; onSelect: (p: Plan) => void }) {
  const price = annual ? plan.priceAnnual : plan.priceMonthly;
  const isFree = plan.id === "free";
  const isEnterprise = plan.action === "contact";

  return (
    <article
      className={`relative flex w-[82vw] shrink-0 snap-start flex-col rounded-3xl border p-5 md:w-auto ${
        plan.popular ? "border-accent-neon/40 bg-accent-neon/[0.04]" : "border-border bg-card/50"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-accent-neon px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-background">
          Popular
        </span>
      )}

      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-neon">{plan.name}</p>

      <div className="mt-4 flex min-h-[52px] flex-wrap items-baseline gap-x-2">
        <span className={`font-semibold tracking-tight text-foreground ${isEnterprise ? "text-3xl" : "text-4xl"}`}>
          {price}
        </span>
        {plan.unit && <span className="text-[11.5px] uppercase tracking-wider text-muted-foreground">{plan.unit}</span>}
      </div>
      <p className="mt-1 min-h-[16px] text-[11px] text-muted-foreground/80">
        {plan.priceNote
          ? plan.priceNote
          : annual && plan.annualNote
            ? plan.annualNote
            : isFree
              ? "No card required"
              : ""}
      </p>

      <button
        type="button"
        onClick={() => !plan.disabled && onSelect(plan)}
        disabled={plan.disabled}
        aria-disabled={plan.disabled}
        className={`mt-5 inline-flex h-11 items-center justify-center rounded-full px-4 text-[13px] font-medium transition-opacity ${
          plan.disabled
            ? "cursor-not-allowed border border-dashed border-border bg-transparent text-muted-foreground"
            : "hover:opacity-90"
        } ${
          plan.disabled
            ? ""
            : plan.popular
            ? "bg-primary text-primary-foreground"
            : "border border-border bg-card/60 text-foreground/90"
        }`}
      >
        {plan.cta}
      </button>

      <p className="mt-6 text-[11px] font-semibold uppercase tracking-wider text-foreground/70">{plan.featuresTitle}</p>
      <ul className="mt-3 space-y-2.5">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2 text-[13px] leading-relaxed text-muted-foreground">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-neon" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}